import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { UserHelperService } from '@helpers/user-helper.service';
import { AclAbilityService } from '@services/acl/acl.ability';
import { ResourceNames } from '@enums/acl/resources.enum';
import { SesSendingNotificationI, SesSendingNotificationListI } from '@interfaces/email-sending-health.interface';

/**
 * Durable sending-health notifications (SES-D20).
 *
 * Durable, and not the existing general-notification channel, for two reasons:
 * that channel is a socket event plus a `BehaviorSubject`, so whoever was not
 * logged in when sending stopped never learns of it; and its read state is
 * `localStorage`, so the same alert comes back on every other device. Here the
 * list is loaded from the server at boot and the acknowledgement is persisted
 * with who did it.
 */
@Injectable({ providedIn: 'root' })
export class EmailSendingHealthNotificationService {
  private readonly baseUrl = `${environment.OMNISMART_API_MARKETING_CAMPAIGN}ses-health/notifications`;

  private readonly notifications = new BehaviorSubject<SesSendingNotificationI[]>([]);
  public notifications$ = this.notifications.asObservable();

  private initialized = false;

  constructor(
    private readonly http: HttpClient,
    private readonly userHelper: UserHelperService,
    private readonly acl: AclAbilityService
  ) {}

  /**
   * The ACL resource, not the profile level: it ships granted from admin
   * upwards, and a supervisor whose own admin deliberately granted it must get
   * the alert too - otherwise they can open the screen and never hear about it.
   */
  private get allowed(): boolean {
    return this.acl.canListAll(ResourceNames.CAMPAIGNS__EMAIL_HEALTH);
  }

  public get current(): SesSendingNotificationI[] {
    return this.notifications.getValue();
  }

  /** Only the unread ones count towards the badge; the read ones stay listed. */
  public get openCount(): number {
    return this.notifications.getValue().filter((item) => !item.acknowledged).length;
  }

  /**
   * Initial load, skipped for whoever cannot open the screen: no pointless
   * request, and no 403 logged on every boot.
   */
  public async initOnce(): Promise<void> {
    if (this.initialized || !this.allowed) {
      return;
    }
    this.initialized = true;
    await this.reload();
  }

  public async reload(): Promise<void> {
    try {
      // The read ones come along on purpose: an alert that vanishes the moment
      // it is acknowledged leaves the admin with no way to read again what
      // stopped their sending and what to do about it.
      const params = new HttpParams().set('limit', '20').set('includeAcknowledged', 'true');
      const response = await firstValueFrom(this.http.get<SesSendingNotificationListI>(this.baseUrl, { params }));
      this.notifications.next(response?.notifications ?? []);
    } catch (error) {
      // A customer with no breaker state has nothing here; never block boot.
      this.notifications.next([]);
    }
  }

  /** Called by the socket listener; de-duplicated by document id. */
  public push(notification: SesSendingNotificationI): void {
    if (!notification?._id || !this.allowed) {
      return;
    }
    const current = this.notifications.getValue();
    if (current.some((item) => item._id === notification._id)) {
      return;
    }
    this.notifications.next([notification, ...current]);
  }

  public async acknowledge(notification: SesSendingNotificationI): Promise<void> {
    const customer = this.customerId();
    try {
      await firstValueFrom(this.http.post(`${this.baseUrl}/${notification._id}/ack`, { customer }));
    } catch (error) {
      // Best-effort: the caller awaits this before navigating to the screen, so
      // rejecting here would swallow the navigation the click was for. Nothing
      // was recorded server-side, so the alert comes back on the next boot.
    }

    // Marked, never removed - including when another admin of the same customer
    // got there first and the route answered `acknowledged: false`. The alert
    // stays consultable; what changes is that it stops counting as new.
    const acknowledged = { name: this.userName(), at: new Date().toISOString() };
    this.notifications.next(
      this.notifications
        .getValue()
        .map((item) =>
          item._id === notification._id ? { ...item, acknowledged: item.acknowledged ?? acknowledged } : item
        )
    );
  }

  private customerId(): string {
    const attributes = this.userHelper.getUserAttributes();
    return attributes ? attributes['custom:customer'] : '';
  }

  private userName(): string {
    const attributes = this.userHelper.getUserAttributes();
    return attributes ? attributes['name'] ?? '' : '';
  }
}
